import React from 'react';
import { useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getShownDifference from 'services/numbers/getShownDifference';
import getContributionMarginTotal from 'services/products/calculations/getContributionMarginTotal';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getOfferQuantities from 'services/offers/getOfferQuantities';
import getPositionHours, {
  isAverageHourlyEarningsMissing,
} from 'services/offers/getPositionHours';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import translations from 'services/translations/translations';

import { getMarginClass } from '../offerProduct/calculationWindow/getMarginRatioClassName';

import 'style/offers/MarginRatio.css';
import 'style/offers/OfferProductPriceSummary.css';

interface IFigures {
  contributionMargin: number;
  expense: number;
  hours: number;
  missingAverageHourlyEarnings: number;
  quantities: string;
  sellingPrice: number;
}

/**
 * The prices of the offer by work type in one table, in the order of the positions (as
 * in the offer PDF), the whole offer last. The margin % has the same marks as in the
 * calculation window.
 */
const OfferProductPriceSummary = ({
  products,
}: {
  products: IOfferProductsGet[];
}): JSX.Element => {
  const { t } = useTranslation([
    translations.common,
    translations.offers,
    translations.products,
  ]);

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories ?? []
  );

  const getFigures = (positions: IOfferProductsGet[]): IFigures =>
    positions.reduce(
      (figures, product) => {
        const primaryVersion = product.versions.find(
          (version) => version.isPrimary
        );

        if (primaryVersion === undefined) {
          return figures;
        }

        const { averageHourlyEarnings, installations, subproducts } =
          primaryVersion;

        return {
          ...figures,
          contributionMargin:
            figures.contributionMargin +
            getContributionMarginTotal(
              installations,
              subproducts,
              product.quantity
            ),
          expense:
            figures.expense +
            getExpenseTotal(installations, subproducts, product.quantity),
          hours:
            figures.hours +
            getPositionHours(
              installations,
              averageHourlyEarnings,
              product.quantity,
              installationCategories
            ),
          // Without the average hourly earnings installation work has no hours, which
          // makes the margin per hour too high
          missingAverageHourlyEarnings:
            figures.missingAverageHourlyEarnings +
            (isAverageHourlyEarningsMissing(
              installations,
              averageHourlyEarnings,
              installationCategories
            )
              ? 1
              : 0),
          sellingPrice:
            figures.sellingPrice +
            getSellingPriceTotal(installations, subproducts, product.quantity),
        };
      },
      {
        contributionMargin: 0,
        expense: 0,
        hours: 0,
        missingAverageHourlyEarnings: 0,
        // Quantities by unit, batch positions by their batch calculation review
        quantities: Object.entries(getOfferQuantities(positions))
          .map(
            ([unit, quantity]) =>
              `${formatGroupedNumber(quantity, 2, 0)} ${t(
                `${translations.common}:units:${unit}`
              )}`
          )
          .join(', '),
        sellingPrice: 0,
      }
    );

  // The work types in the order of the positions
  const groups = products
    .map((product) => product.group)
    .filter((group, index, all) => all.indexOf(group) === index);

  const total = getFigures(products);

  const renderRow = (title: string, figures: IFigures, className = '') => {
    const ratio =
      figures.sellingPrice === 0
        ? 0
        : (figures.contributionMargin / figures.sellingPrice) * 100;

    return (
      <tr className={className} key={title}>
        <th scope="row">{title}</th>
        <td className="quantities">{figures.quantities || '-'}</td>
        <td className="numeric">
          {`${formatGroupedNumber(figures.sellingPrice)} €`}
        </td>
        <td className="numeric">{`${formatGroupedNumber(
          figures.expense
        )} €`}</td>
        <td className="numeric">
          {/* The shown selling price minus the shown expense, so the row adds up */}
          {`${formatGroupedNumber(
            getShownDifference(figures.sellingPrice, figures.expense)
          )} €`}
        </td>
        <td className="numeric">
          <span
            className={`margin-ratio ${
              figures.sellingPrice === 0 ? '' : getMarginClass(ratio)
            }`}
          >
            {`${formatGroupedNumber(ratio)} %`}
          </span>
        </td>
        <td className="numeric">{`${formatGroupedNumber(figures.hours)} h`}</td>
        <td className="numeric">
          {figures.hours === 0
            ? '-'
            : `${formatGroupedNumber(
                figures.contributionMargin / figures.hours
              )} €/h`}
        </td>
      </tr>
    );
  };

  return (
    <div className="offer-product-price-summary">
      <h3 className="compact-header">
        {t(`${translations.common}:Price`).toUpperCase()}
      </h3>
      <div className="price-summary-table">
        <Table size="sm">
          <thead>
            <tr>
              <th>{t(`${translations.offers}:Work type`)}</th>
              <th>{t(`${translations.offers}:Quantities`)}</th>
              <th className="numeric">
                {t(`${translations.offers}:Selling price`)}
              </th>
              <th className="numeric">
                {t(`${translations.offers}:Expenses total`)}
              </th>
              <th className="numeric">
                {`${t(`${translations.offers}:Contribution margin`)} €`}
              </th>
              <th className="numeric">
                {`${t(`${translations.offers}:Contribution margin`)} %`}
              </th>
              <th className="numeric">{t(`${translations.offers}:Hours`)}</th>
              <th className="numeric">
                {t(`${translations.offers}:Contribution margin per hour`)}
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) =>
              renderRow(
                t(`${translations.products}:groups:${group}`),
                getFigures(
                  products.filter((product) => product.group === group)
                )
              )
            )}
            {renderRow(
              t(`${translations.offers}:Whole offer total`),
              total,
              'price-summary-total'
            )}
          </tbody>
        </Table>
      </div>
      {total.missingAverageHourlyEarnings > 0 ? (
        <span className="missing-average-hourly-earnings">
          {t(
            `${translations.offers}:Positions missing average hourly earnings`,
            { count: total.missingAverageHourlyEarnings }
          )}
        </span>
      ) : null}
    </div>
  );
};

export default OfferProductPriceSummary;
